import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';

export class Mushroom implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private health: number = 4; // Takes 4 hits to destroy
  private maxHealth: number = 4;
  private isPoisoned: boolean = false;

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: 0 };
    this.size = { x: 16, y: 16 };
    this.renderManager = renderManager;
  }

  public update(deltaTime: number): void {
    // Mushrooms are static
  }

  public hit(): number {
    this.health--;
    
    if (this.health <= 0) {
      this.active = false;
      return 1; // Official: 1 point for totally eliminating mushroom
    }
    
    return 5; // Official: 5 points for each hit (shot 1 to 3 times)
  }

  public repair(): void {
    this.health = Math.min(this.maxHealth, this.health + 1);
    if (this.health > 0) {
      this.active = true;
    }
  }

  public isFullHealth(): boolean {
    return this.health >= this.maxHealth;
  }

  public getHealth(): number {
    return this.health;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;

    // Different patterns based on health
    let pattern: number[][];
    let color: string = '#FF00FF'; // Default bright magenta
    
    // Poison mushrooms are different color (manual mentions "different color")
    if (this.isPoisoned) {
      // Poison mushrooms are darker/greenish
      color = this.health === 4 ? '#008000' : 
              this.health === 3 ? '#40A040' : 
              this.health === 2 ? '#60C060' : '#80E080';
    }
    
    switch (this.health) {
      case 4: // Full health
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,0,1,1,0,1,1],
          [1,1,1,1,1,1,1,1],
          [1,1,1,0,0,1,1,1],
          [1,1,1,1,1,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0]
        ];
        if (!this.isPoisoned) color = '#FF00FF'; // Bright magenta
        break;
      case 3:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,0,1,1,0,1,1],
          [1,1,1,1,1,1,1,1],
          [1,1,1,0,0,1,1,1],
          [1,0,1,1,1,1,0,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0]
        ];
        if (!this.isPoisoned) color = '#FF4080'; // Lighter magenta
        break;
      case 2:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,0,0,1,1,0,0,1],
          [1,1,1,1,1,1,1,1],
          [1,0,1,0,0,1,0,1],
          [1,0,1,1,1,1,0,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,1,1,1,0,0]
        ];
        if (!this.isPoisoned) color = '#FF80C0'; // Even lighter
        break;
      case 1:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,0,1,1,0,1,0],
          [1,0,0,1,1,0,0,1],
          [1,0,1,1,1,1,0,1],
          [1,0,1,0,0,1,0,1],
          [1,0,0,1,1,0,0,1],
          [0,1,0,1,1,0,1,0],
          [0,0,1,1,1,1,0,0]
        ];
        if (!this.isPoisoned) color = '#FFBFDF'; // Very light, almost damaged
        break;
      default:
        return; // Should not render if health is 0
    }

    const pixelSize = 2;

    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - (pattern[row].length * pixelSize) / 2 + col * pixelSize;
          const y = centerY - (pattern.length * pixelSize) / 2 + row * pixelSize;
          this.renderManager.fillRect(x, y, pixelSize, pixelSize, color);
        }
      }
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public poison(): void {
    this.isPoisoned = true;
  }

  public isPoisonMushroom(): boolean {
    return this.isPoisoned;
  }

  public cure(): void {
    this.isPoisoned = false;
  }
}